import { createContext, useContext, useEffect, useState } from 'react'
import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAdminAuth, AdminAuthProvider } from './hooks/useAdminAuth'
import { adminTheme } from './theme'
import AdminLayout from './components/AdminLayout'
import ListingImportPage from './pages/ListingImportPage'
import BnbEnrichmentPage from './pages/BnbEnrichmentPage'
import AirbnbScrapePage from './pages/AirbnbScrapePage'
import { fetchHealth } from './services/adminApi'
import './admin.css'

type AdminJobContextValue = {
  setJobRunning: (v: boolean) => void
}

const AdminJobContext = createContext<AdminJobContextValue>({ setJobRunning: () => {} })

export function useAdminJob() {
  return useContext(AdminJobContext)
}

function LoginScreen({
  onLogin,
  loading,
  error,
}: {
  onLogin: (u: string, p: string) => Promise<void>
  loading: boolean
  error: string | null
}) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  return (
    <Box className="admin-login-bg admin-app">
      <Box className="admin-login-card">
        <Box className="admin-login-logo">A</Box>
        <Box component="h1" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.75rem', m: 0 }}>
          AonlaStay <span className="admin-gold-text">Admin</span>
        </Box>
        <Box component="p" sx={{ color: 'text.secondary', mb: 3, mt: 0.5, fontSize: 14 }}>
          Listing import · Airbnb scrape · BNB enrichment
        </Box>
        {error && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(214,69,69,0.1)', borderRadius: 2, color: '#b83232', fontSize: 14 }}>
            {error}
          </Box>
        )}
        <Box component="form" onSubmit={(e) => { e.preventDefault(); void onLogin(username, password) }}>
          <Box sx={{ mb: 2 }}>
            <label htmlFor="admin-user" style={{ fontSize: 12, fontWeight: 600 }}>Username</label>
            <input id="admin-user" className="admin-login-input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" style={{ width: '100%', marginTop: 6, padding: '12px 14px', borderRadius: 8, border: '1px solid rgba(20,24,32,0.12)' }} />
          </Box>
          <Box sx={{ mb: 2.5 }}>
            <label htmlFor="admin-pass" style={{ fontSize: 12, fontWeight: 600 }}>Password</label>
            <input id="admin-pass" type="password" className="admin-login-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" style={{ width: '100%', marginTop: 6, padding: '12px 14px', borderRadius: 8, border: '1px solid rgba(20,24,32,0.12)' }} />
          </Box>
          <button type="submit" disabled={loading || !username || !password} className="admin-login-btn" style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #C9A227, #E8D5A3)', fontWeight: 700, cursor: 'pointer' }}>
            {loading ? 'Signing in…' : 'Sign in to console'}
          </button>
        </Box>
      </Box>
    </Box>
  )
}

function AdminShell() {
  const [apiOnline, setApiOnline] = useState(true)
  const [jobRunning, setJobRunning] = useState(false)

  useEffect(() => {
    fetchHealth()
      .then((h) => setApiOnline(String(h?.status).includes('ok')))
      .catch(() => setApiOnline(false))
    const id = window.setInterval(() => {
      fetchHealth()
        .then((h) => setApiOnline(String(h?.status).includes('ok')))
        .catch(() => setApiOnline(false))
    }, 30000)
    return () => window.clearInterval(id)
  }, [])

  return (
    <AdminJobContext.Provider value={{ setJobRunning }}>
      <AdminLayout apiOnline={apiOnline} isRunning={jobRunning}>
        <Routes>
          <Route index element={<Navigate to="import" replace />} />
          <Route path="import" element={<ListingImportPage />} />
          <Route path="bnb-enrichment" element={<BnbEnrichmentPage />} />
          <Route path="airbnb-scraper" element={<AirbnbScrapePage />} />
          <Route path="*" element={<Navigate to="import" replace />} />
        </Routes>
      </AdminLayout>
    </AdminJobContext.Provider>
  )
}

export default function AdminConsole() {
  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline />
      <AdminAuthProvider>
        <AdminGate />
      </AdminAuthProvider>
    </ThemeProvider>
  )
}

function AdminGate() {
  const { loading, error, login, isAuthenticated } = useAdminAuth()

  if (loading) {
    return (
      <Box className="admin-app admin-login-bg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#C9A227' }} size={40} />
      </Box>
    )
  }

  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} loading={loading} error={error} />
  }

  return <AdminShell />
}
